import { useState, useEffect, useCallback } from 'react';
import { getCategoryConfig } from '../../databaseManager';
import { DEFAULT_CATEGORY_CONFIGS, LATER_ADDED_COLUMNS } from './defaultCategoryConfigs';

// A saved config predates any column added since — append those (hidden) so
// they're reachable from the column menu without resetting the customer's layout.
function withLaterColumns(config) {
  if (!config?.columns) return config;
  const ids = new Set(config.columns.map((c) => c.id));
  const missing = LATER_ADDED_COLUMNS.filter((c) => !ids.has(c.id)).map((c) => ({ ...c, visible: false }));
  return missing.length ? { ...config, columns: [...config.columns, ...missing] } : config;
}

/**
 * Load the CategoryConfig for a given customer + category key.
 * Falls back to the hardcoded default if no config is saved in Firestore.
 */
export function useProductConfig(customerId, categoryKey) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!customerId || !categoryKey) {
      setConfig(DEFAULT_CATEGORY_CONFIGS[categoryKey] || DEFAULT_CATEGORY_CONFIGS.default);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const saved = await getCategoryConfig(customerId, categoryKey);
      setConfig(saved ? withLaterColumns(saved) : (DEFAULT_CATEGORY_CONFIGS[categoryKey] || DEFAULT_CATEGORY_CONFIGS.default));
    } catch (err) {
      setError(err);
      setConfig(DEFAULT_CATEGORY_CONFIGS[categoryKey] || DEFAULT_CATEGORY_CONFIGS.default);
    } finally {
      setLoading(false);
    }
  }, [customerId, categoryKey]);

  useEffect(() => { load(); }, [load]);

  return { config, setConfig, loading, error, refreshConfig: load };
}
